import React, { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal2, Modal2Footer, Modal2Button, Select, Input, RadioButton, Button } from "../../../common";
import { TextArea } from "../../../common/TextArea";
import { Plus, Trash2, Check } from "lucide-react";
import { WorkflowVisualization } from "./WorkflowVisualization";
import { capitalizeWords } from "../../../../utils/nameUtils";
import { useGetApprovalGroupsQuery, IWorkflowDefinition } from "../../../../store/apis/dynamicWorkflow.api";
import { MODULES } from "../../../../utils/dynamicworkflow/constants";

interface IWorkflowLevel {
    groupId: string;
    sequence: number;
    canBeSkipped: boolean;
}

interface IEditWorkflowDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workflow: IWorkflowDefinition | null;
    onSave: (data: {
        module: string;
        name: string;
        description: string;
        levels: IWorkflowLevel[];
        isActive: boolean;
        canAdminApprove: boolean;
    }) => void;
    isUpdating?: boolean;
}

const emptyLevel: IWorkflowLevel = {
    groupId: "",
    sequence: 1,
    canBeSkipped: false,
};

export function EditWorkflowDialog({
    open,
    onOpenChange,
    workflow,
    onSave,
    isUpdating
}: IEditWorkflowDialogProps) {
    const { data: groupsResponse, isLoading: isLoadingGroups } = useGetApprovalGroupsQuery();

    const [module, setModule] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [levels, setLevels] = useState<IWorkflowLevel[]>([{ ...emptyLevel }]);
    const [canAdminApprove, setCanAdminApprove] = useState(true);
    const [status, setStatus] = useState("");

    const moduleOptions = Object.values(MODULES).map((m) => ({
        label: m.label,
        value: m.category_code,
    }));

    const groupOptions = useMemo(() => {
        if (!groupsResponse?.data?.items) return [];
        return groupsResponse.data.items
            .filter((s) => s.id !== undefined)
            .map((s) => ({
                value: s.id ?? "",
                label: s.isSystemGroup ? `${s.name} (System)` : s.name,
            }));
    }, [groupsResponse]);

    useEffect(() => {
        if (open && workflow) {
            setModule(workflow.permissionCode || "");
            setName(capitalizeWords(workflow.name || ""));
            setDescription(workflow.description || "");
            setCanAdminApprove(workflow.canAdminApproveMidFlow ?? true);
            setStatus(workflow.isActive ? "true" : "false");

            const savedLevels = workflow.steps || [];
            if (savedLevels.length > 0) {
                setLevels(savedLevels.map((step) => ({
                    groupId: step.groupId,
                    sequence: step.sequence,
                    canBeSkipped: step.canBeSkipped,
                })));
            } else {
                setLevels([{ ...emptyLevel }]);
            }
        }
    }, [open, workflow]);

    const addLevel = () => {
        setLevels((p) => [
            ...p,
            { ...emptyLevel, sequence: p.length + 1 }
        ]);
    };

    const removeLevel = (index: number) => {
        if (levels.length > 0) {
            setLevels((p) => {
                const newLevels = p.filter((_, i) => i !== index);
                return newLevels.map((level, i) => ({ ...level, sequence: i + 1 }));
            });
        }
    };

    const updateLevel = (index: number, updates: Partial<IWorkflowLevel>) => {
        setLevels((prev) =>
            prev.map((l, i) => (i === index ? { ...l, ...updates } : l))
        );
    };

    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        if (!module || !name || !workflow) return;

        if (levels.length === 0) {
            toast.error("Please add at least one level");
            return;
        }

        const hasIncompleteLevels = levels.some((level) => !level.groupId);
        if (hasIncompleteLevels) {
            toast.error("Please select an approval group for all levels");
            return;
        }

        onSave({ module, name, description, levels, canAdminApprove, isActive: status !== "false" });
    };

    const previewLevels = [
        { label: "E", type: "employee" as const },
        ...levels.map((_, i) => ({ label: `L${i + 1}`, type: "level" as const })),
    ];

    const formId = "edit-workflow-form";

    return (
        <Modal2
            isOpen={open}
            onClose={() => onOpenChange(false)}
            title="Edit Workflow"
            subtitle="Update workflow configuration"
            width={560}
            maskClosable={false}
            loading={isUpdating}
            footer={
                <Modal2Footer>
                    <Modal2Button variant="secondary" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Modal2Button>
                    <Button
                        appearance="primary"
                        loading={isUpdating}
                        htmlType="submit"
                        form={formId}
                        disabled={!module || !name || isUpdating}
                    >
                        <Check className="w-[15px] h-[15px]" />
                        Update Workflow
                    </Button>
                </Modal2Footer>
            }
        >
            <form id={formId} onSubmit={handleSave} className="space-y-5">
                <Input
                    label="Workflow Name"
                    required
                    value={name}
                    placeholder="Enter workflow name"
                    onChange={(v) => setName(String(v ?? ""))}
                />

                <TextArea
                    label="Description"
                    value={description}
                    placeholder="Enter workflow description"
                    onChange={(v: string) => setDescription(v)}
                    minRows={3}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Select
                        label="Module"
                        disabled
                        required
                        value={module}
                        onChange={(v) => setModule(String(v ?? ""))}
                        options={moduleOptions}
                        placeholder="Select module"
                        searchable
                    />
                    <div className="flex flex-col gap-1.5">
                        <label className="block text-xs font-semibold text-gray-700 mb-[7px]">
                            Status <span className="text-[var(--color-error)] ml-1">*</span>
                        </label>
                        <div className="flex items-center gap-4 h-[38px]">
                            <RadioButton
                                options={[
                                    { label: "Active", value: "true" },
                                    { label: "Inactive", value: "false" },
                                ]}
                                value={status}
                                onChange={(v) => setStatus(String(v ?? ""))}
                                name="status"
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-[var(--color-bg-muted)] border border-[var(--color-border-secondary)] rounded-[13px] p-4 flex justify-center">
                    <WorkflowVisualization levels={previewLevels} />
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-[13.5px] font-bold text-[var(--color-text-primary)] mb-0">
                            Approval Levels
                        </h3>
                    </div>

                    <div className="flex flex-col gap-[10px]">
                        {levels.map((level, index) => (
                            <div
                                key={index}
                                className="flex flex-col gap-3 p-[14px] bg-[var(--color-bg-base)] border border-[var(--color-border-secondary)] rounded-xl"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="w-[26px] h-[26px] rounded-lg flex-none grid place-items-center bg-[var(--color-primary-600)] text-white text-xs font-bold">
                                            {level.sequence}
                                        </span>
                                        <span className="text-[13px] font-bold text-[var(--color-text-primary)]">
                                            Level {level.sequence}
                                        </span>
                                    </div>
                                    {levels.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => removeLevel(index)}
                                            className="p-1.5 rounded-[9px] text-[var(--color-text-tertiary)] hover:text-[var(--color-error-dark)] hover:bg-[var(--color-error-light)] transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                <Select
                                    label="Approval Group"
                                    value={level.groupId}
                                    onChange={(v) => updateLevel(index, { groupId: String(v ?? "") })}
                                    options={groupOptions}
                                    placeholder={isLoadingGroups ? "Loading groups..." : "Select group"}
                                    searchable
                                />

                                <div className="flex flex-wrap gap-x-6 gap-y-3 pt-1">
                                    <label className={`flex items-center gap-2 text-xs ${index === levels.length - 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-500 font-medium"}`}>
                                        <input
                                            type="checkbox"
                                            checked={level.canBeSkipped && index !== levels.length - 1}
                                            disabled={index === levels.length - 1}
                                            onChange={(e) =>
                                                updateLevel(index, { canBeSkipped: e.target.checked })
                                            }
                                            className="w-4 h-4 text-[var(--color-primary-600)] rounded-lg border-[var(--color-border-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        Can Be Skipped
                                    </label>
                                </div>
                            </div>
                        ))}

                        <Button
                            appearance="dashed"
                            onClick={addLevel}
                            className="w-full h-[42px] text-[var(--color-primary-600)] border-[var(--color-border-primary)] hover:bg-[var(--color-bg-muted)] flex items-center justify-center"
                        >
                            <div className="flex items-center gap-[7px]">
                                <Plus className="w-[15px] h-[15px]" />
                                <span className="font-bold text-[12.5px]">Add level</span>
                            </div>
                        </Button>
                    </div>
                </div>

                <div className="border border-[var(--color-border-secondary)] rounded-[13px] p-4 bg-[var(--color-bg-muted)]">
                    <label className="flex items-center gap-2 text-[13px] text-gray-700 font-semibold">
                        <input
                            type="checkbox"
                            checked={canAdminApprove}
                            onChange={(e) => setCanAdminApprove(e.target.checked)}
                            className="w-4 h-4 text-[var(--color-primary-600)] rounded border-[var(--color-border-primary)] cursor-pointer"
                        />
                        Admin can approve mid-flow
                    </label>
                </div>
            </form>
        </Modal2>
    );
}
